import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { projectsAPI, tasksAPI, resourcesAPI } from '../services/api';
import { PROJECT_STATUS as PROJECT_STATUS_ENUM } from '../constants/projectEnums';
import { TASK_STATUS as TASK_STATUS_ENUM } from '../constants/taskEnums';
import { RESOURCE_TYPE_VALUES } from '../constants/resourceTypes';

const ProjectContext = createContext(null);

// Task status types
export const TASK_STATUS = TASK_STATUS_ENUM;

// Project status types
export const PROJECT_STATUS = PROJECT_STATUS_ENUM;

// Calculate progress from status
const getProgressFromStatus = (status) => {
  switch (status) {
    case TASK_STATUS.NOT_STARTED:
      return 0;
    case TASK_STATUS.IN_PROGRESS:
      return 50;
    case TASK_STATUS.ON_HOLD:
      return 25;
    case TASK_STATUS.COMPLETED:
      return 100;
    default:
      return 0;
  }
};

// Calculate task progress - use direct progress value or calculate from subtasks/status
const calculateTaskProgress = (task) => {
  // If task has direct progress value, use it
  if (task.progress !== undefined && task.progress !== null) {
    return task.progress;
  }
  // If task has subtasks, calculate from subtask statuses
  if (task.subtasks && task.subtasks.length > 0) {
    const totalProgress = task.subtasks.reduce((sum, st) => sum + getProgressFromStatus(st.status), 0);
    return Math.round(totalProgress / task.subtasks.length);
  }
  // Otherwise calculate from task status
  return getProgressFromStatus(task.status);
};

// Calculate project progress from tasks
const calculateProjectProgress = (tasks) => {
  if (!tasks || tasks.length === 0) return 0;

  let totalProgress = 0;
  tasks.forEach((task) => {
    totalProgress += calculateTaskProgress(task);
  });

  return Math.round(totalProgress / tasks.length);
};

// Determine project status from progress
const getProjectStatusFromProgress = (progress) => {
  if (progress === 0) return PROJECT_STATUS.NOT_STARTED;
  if (progress === 100) return PROJECT_STATUS.COMPLETED;
  return PROJECT_STATUS.IN_PROGRESS;
};

// Mock users - each user can belong to multiple teams
export const mockUsers = [
  { id: '1', name: 'John Smith', role: 'manager', teamIds: ['team-app-dev'] },
  { id: '2', name: 'Sarah Johnson', role: 'developer', teamIds: ['team-frontend', 'team-qa'] },
  { id: '3', name: 'Mike Wilson', role: 'developer', teamIds: ['team-backend'] },
  { id: '4', name: 'Emily Davis', role: 'developer', teamIds: ['team-frontend'] },
  { id: '5', name: 'David Brown', role: 'developer', teamIds: ['team-backend', 'team-devops'] },
  { id: '6', name: 'Lisa Anderson', role: 'QA', teamIds: ['team-qa'] },
  { id: '7', name: 'James Taylor', role: 'DBA', teamIds: ['team-dba'] },
  { id: '8', name: 'Maria Garcia', role: 'contractor', teamIds: ['team-frontend'] },
];

// Mock teams/groups - supports nested teams (team can contain other teams or users)
// parentTeamId: parent team, childTeamIds: child teams, memberIds: direct user members
export const mockTeams = [
  // Top-level department teams
  {
    id: 'team-app-dev',
    name: 'Application Development',
    type: 'team',
    parentTeamId: null,
    childTeamIds: ['team-developers', 'team-qa'],
    memberIds: ['1'], // Project Manager
  },
  {
    id: 'team-infrastructure',
    name: 'Infrastructure',
    type: 'team',
    parentTeamId: null,
    childTeamIds: ['team-dba', 'team-cloud', 'team-devops'],
    memberIds: [],
  },
  // Developer teams (under Application Development)
  {
    id: 'team-developers',
    name: 'Developers',
    type: 'team',
    parentTeamId: 'team-app-dev',
    childTeamIds: ['team-frontend', 'team-backend'],
    memberIds: [],
  },
  {
    id: 'team-frontend',
    name: 'Frontend Developers',
    type: 'team',
    parentTeamId: 'team-developers',
    childTeamIds: [],
    memberIds: ['2', '4', '8'], // Sarah, Emily, Maria (contractor)
  },
  {
    id: 'team-backend',
    name: 'Backend Developers',
    type: 'team',
    parentTeamId: 'team-developers',
    childTeamIds: [],
    memberIds: ['3', '5'], // Mike, David
  },
  // QA team (under Application Development)
  {
    id: 'team-qa',
    name: 'QA',
    type: 'team',
    parentTeamId: 'team-app-dev',
    childTeamIds: [],
    memberIds: ['2', '6'], // Sarah (cross-team), Lisa
  },
  // Infrastructure teams
  {
    id: 'team-dba',
    name: 'DBA Team',
    type: 'team',
    parentTeamId: 'team-infrastructure',
    childTeamIds: [],
    memberIds: ['7'], // James
  },
  {
    id: 'team-cloud',
    name: 'Cloud Team',
    type: 'team',
    parentTeamId: 'team-infrastructure',
    childTeamIds: [],
    memberIds: ['5'], // David (cross-team)
  },
  {
    id: 'team-devops',
    name: 'DevOps',
    type: 'team',
    parentTeamId: 'team-infrastructure',
    childTeamIds: [],
    memberIds: ['5'], // David
  },
  {
    id: 'team-cybersecurity',
    name: 'Cybersecurity',
    type: 'team',
    parentTeamId: null,
    childTeamIds: [],
    memberIds: [],
  },
];

// Helper function to get team by ID
export const getTeamById = (teamId) => mockTeams.find((t) => t.id === teamId);

// Helper function to get user's teams
export const getUserTeams = (userId) => {
  const user = mockUsers.find((u) => u.id === userId);
  if (!user || !user.teamIds) return [];
  return user.teamIds.map((tid) => getTeamById(tid)).filter(Boolean);
};

// Helper function to get team members (direct + from child teams)
export const getTeamMembers = (teamId, includeChildTeams = false) => {
  const team = getTeamById(teamId);
  if (!team) return [];

  let memberIds = [...(team.memberIds || [])];

  if (includeChildTeams && team.childTeamIds) {
    team.childTeamIds.forEach((childId) => {
      const childMembers = getTeamMembers(childId, true);
      childMembers.forEach((m) => {
        if (!memberIds.includes(m.id)) {
          memberIds.push(m.id);
        }
      });
    });
  }

  return memberIds.map((uid) => mockUsers.find((u) => u.id === uid)).filter(Boolean);
};

// Helper function to get child teams recursively
export const getChildTeams = (teamId) => {
  const team = getTeamById(teamId);
  if (!team || !team.childTeamIds || team.childTeamIds.length === 0) return [];

  let children = [];
  team.childTeamIds.forEach((childId) => {
    const childTeam = getTeamById(childId);
    if (childTeam) {
      children.push(childTeam);
      children = [...children, ...getChildTeams(childId)];
    }
  });
  return children;
};

// Helper function to get top-level teams (no parent)
export const getTopLevelTeams = () => mockTeams.filter((t) => !t.parentTeamId);

// Resource types
export const RESOURCE_TYPES = {
  SERVER: RESOURCE_TYPE_VALUES.SERVER,
  DATABASE: RESOURCE_TYPE_VALUES.DATABASE,
  FIREWALL: RESOURCE_TYPE_VALUES.FIREWALL,
  CDN: RESOURCE_TYPE_VALUES.CDN,
  API: RESOURCE_TYPE_VALUES.API,
  SERVICE: RESOURCE_TYPE_VALUES.SERVICE,
};

export const ProjectProvider = ({ children }) => {
  const { getActiveCompany } = useAuth();
  const [projects, setProjects] = useState([]);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState('');

  const activeCompany = useMemo(() => getActiveCompany(), [getActiveCompany]);
  const activeCompanyId = activeCompany?.id || null;

  const normalizeSubtask = useCallback((subtask) => ({
    id: subtask.id,
    name: subtask.name || '',
    status: subtask.status || TASK_STATUS.NOT_STARTED,
    assigneeId: subtask.assigneeId || subtask.ownerId || subtask.owner_id || '',
    assignee: subtask.assignee || subtask.ownerName || subtask.owner_name || '',
  }), []);

  const normalizeTask = useCallback((task) => ({
    id: task.id,
    name: task.name || '',
    description: task.description || '',
    notes: task.notes || '',
    status: task.status || TASK_STATUS.NOT_STARTED,
    assigneeId: task.assigneeId || task.ownerId || task.owner_id || '',
    assignee: task.assignee || task.ownerName || task.owner_name || '',
    dueDate: task.dueDate || task.due_date || '',
    progress: task.progress,
    subtasks: Array.isArray(task.subtasks) ? task.subtasks.map(normalizeSubtask) : [],
  }), [normalizeSubtask]);

  const normalizeResource = useCallback((resource) => ({
    id: resource.id,
    type: resource.type || RESOURCE_TYPE_VALUES.SERVER,
    name: resource.name || '',
    description: resource.description || '',
    details: resource.details || '',
    createdById: resource.createdById || resource.created_by_id || '',
  }), []);

  const normalizeProject = useCallback((project) => ({
    id: project.id,
    name: project.name || '',
    description: project.description || '',
    managerId: project.managerId || project.manager_id || '',
    manager: project.managerName || project.manager_name || 'Unassigned',
    startDate: project.startDate || project.start_date || '',
    dueDate: project.dueDate || project.due_date || '',
    createdAt: project.createdAt || project.created_at || '',
    category: project.category || 'other',
    priority: project.priority || 'medium',
    status: project.status || PROJECT_STATUS.NOT_STARTED,
    createdById: project.createdById || project.created_by_id || '',
    progress: project.progress ?? null,
    taskCount: project.taskCount ?? project.task_count ?? 0,
    tasks: Array.isArray(project.tasks) ? project.tasks.map(normalizeTask) : [],
    resources: Array.isArray(project.resources) ? project.resources.map(normalizeResource) : [],
    detailsLoaded: Boolean(project.detailsLoaded),
  }), [normalizeTask, normalizeResource]);

  const refreshProjects = useCallback(async (options = {}) => {
    if (!activeCompanyId) {
      setProjects([]);
      setProjectsError('');
      return;
    }

    setIsProjectsLoading(true);
    setProjectsError('');

    try {
      const response = await projectsAPI.listByCompany(activeCompanyId, options);
      const normalizedProjects = response.map(normalizeProject);

      setProjects((prev) => normalizedProjects.map((project) => {
        const existing = prev.find((p) => p.id === project.id);
        if (existing?.detailsLoaded) {
          return {
            ...project,
            tasks: existing.tasks,
            resources: existing.resources,
            detailsLoaded: true,
          };
        }
        return project;
      }));
    } catch (error) {
      setProjectsError(error.message || 'Failed to load projects');
      setProjects([]);
    } finally {
      setIsProjectsLoading(false);
    }
  }, [activeCompanyId, normalizeProject]);

  const fetchProjectDetails = useCallback(async (projectId) => {
    if (!projectId) {
      return null;
    }

    // Fetch project first so we can stop immediately on access errors.
    const projectResponse = await projectsAPI.getById(projectId);

    const [tasksResponse, resourcesResponse] = await Promise.all([
      tasksAPI.listByProject(projectId),
      resourcesAPI.listByProject(projectId),
    ]);

    const detailedProject = {
      ...normalizeProject(projectResponse),
      tasks: Array.isArray(tasksResponse) ? tasksResponse.map(normalizeTask) : [],
      resources: Array.isArray(resourcesResponse) ? resourcesResponse.map(normalizeResource) : [],
      detailsLoaded: true,
    };

    setProjects((prev) => {
      const existingIndex = prev.findIndex((project) => project.id === projectId);
      if (existingIndex === -1) {
        return [...prev, detailedProject];
      }

      const next = [...prev];
      next[existingIndex] = detailedProject;
      return next;
    });

    return detailedProject;
  }, [normalizeProject, normalizeResource, normalizeTask]);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  // Get all projects with calculated progress and status
  const getAllProjects = useCallback(() => {
    return projects.map((project) => {
      const tasks = project.tasks || [];
      const progress = tasks.length > 0
        ? calculateProjectProgress(tasks)
        : (project.progress ?? 0);
      const status = project.status
        || (tasks.length > 0
          ? getProjectStatusFromProgress(progress)
          : PROJECT_STATUS.NOT_STARTED);
      return {
        ...project,
        progress,
        status,
      };
    });
  }, [projects]);

  // Get project by ID with calculated progress
  const getProjectById = useCallback((projectId) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return null;

    const tasks = project.tasks || [];
    const progress = tasks.length > 0
      ? calculateProjectProgress(tasks)
      : (project.progress ?? 0);
    const status = project.status
      || (tasks.length > 0
        ? getProjectStatusFromProgress(progress)
        : PROJECT_STATUS.NOT_STARTED);
    return {
      ...project,
      progress,
      status,
    };
  }, [projects]);

  // Get tasks for a project with calculated progress
  const getProjectTasks = useCallback((projectId) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return [];

    return (project.tasks || []).map((task) => ({
      ...task,
      progress: calculateTaskProgress(task),
    }));
  }, [projects]);

  // Add new project
  const addProject = useCallback((projectData) => {
    const newProject = {
      id: `p${Date.now()}`,
      ...projectData,
      tasks: [],
      createdAt: new Date().toISOString().split('T')[0],
    };
    setProjects((prev) => [...prev, newProject]);
    return newProject;
  }, []);

  // Update project
  const updateProject = useCallback(async (projectId, updates) => {
    const payload = {
      ...(updates.name !== undefined ? { name: updates.name } : {}),
      ...(updates.description !== undefined ? { description: updates.description } : {}),
      ...(updates.status !== undefined ? { status: updates.status } : {}),
      ...(updates.managerId !== undefined ? { managerId: updates.managerId } : {}),
      ...(updates.dueDate !== undefined ? { dueDate: updates.dueDate } : {}),
    };

    const updatedProject = await projectsAPI.update(projectId, payload);
    const normalizedProject = normalizeProject(updatedProject);

    setProjects((prev) =>
      prev.map((project) =>
        project.id === projectId
          ? {
            ...project,
            ...normalizedProject,
            tasks: project.tasks || [],
            resources: project.resources || [],
            detailsLoaded: Boolean(project.detailsLoaded),
          }
          : project
      )
    );

    return normalizedProject;
  }, [normalizeProject]);

  // Delete project
  const deleteProject = useCallback((projectId) => {
    setProjects((prev) => prev.filter((p) => p.id !== projectId));
  }, []);

  // Add task to project
  const addTask = useCallback(async (projectId, taskData) => {
    const payload = {
      projectId,
      name: taskData.name,
      description: taskData.description,
      status: taskData.status,
      ownerId: taskData.assigneeId || null,
      dueDate: taskData.dueDate,
    };
    const createdTask = await tasksAPI.create(payload);
    const normalizedTask = normalizeTask(createdTask);

    setProjects((prev) =>
      prev.map((project) =>
        project.id === projectId
          ? { ...project, tasks: [...(project.tasks || []), normalizedTask] }
          : project
      )
    );

    return normalizedTask;
  }, [normalizeTask]);

  // Update task
  const updateTask = useCallback(async (projectId, taskId, updates) => {
    const payload = {
      ...(updates.name !== undefined ? { name: updates.name } : {}),
      ...(updates.description !== undefined ? { description: updates.description } : {}),
      ...(updates.notes !== undefined ? { notes: updates.notes } : {}),
      ...(updates.progress !== undefined ? { progress: updates.progress } : {}),
      ...(updates.status !== undefined ? { status: updates.status } : {}),
      ...(updates.assigneeId !== undefined ? { ownerId: updates.assigneeId || null } : {}),
      ...(updates.dueDate !== undefined ? { dueDate: updates.dueDate } : {}),
    };

    const updatedTask = await tasksAPI.update(taskId, payload);
    const normalizedTask = normalizeTask(updatedTask);

    setProjects((prev) =>
      prev.map((project) =>
        project.id === projectId
          ? {
            ...project,
            tasks: (project.tasks || []).map((task) => (task.id === taskId ? normalizedTask : task)),
          }
          : project
      )
    );
  }, [normalizeTask]);

  // Delete task
  const deleteTask = useCallback(async (projectId, taskId) => {
    await tasksAPI.remove(taskId);
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? { ...p, tasks: (p.tasks || []).filter((t) => t.id !== taskId) }
          : p
      )
    );
  }, []);

  // Add subtask to task
  const addSubtask = useCallback(async (projectId, taskId, subtaskData) => {
    const payload = {
      name: subtaskData.name,
      status: subtaskData.status,
      ownerId: subtaskData.assigneeId || null,
    };
    const createdSubtask = await tasksAPI.createSubtask(taskId, payload);
    const normalizedSubtask = normalizeSubtask(createdSubtask);

    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? {
            ...p,
            tasks: (p.tasks || []).map((t) =>
              t.id === taskId
                ? { ...t, subtasks: [...(t.subtasks || []), normalizedSubtask] }
                : t
            ),
          }
          : p
      )
    );
    return normalizedSubtask;
  }, [normalizeSubtask]);

  // Update subtask
  const updateSubtask = useCallback(async (projectId, taskId, subtaskId, updates) => {
    const payload = {
      ...(updates.name !== undefined ? { name: updates.name } : {}),
      ...(updates.status !== undefined ? { status: updates.status } : {}),
      ...(updates.assigneeId !== undefined ? { ownerId: updates.assigneeId || null } : {}),
    };

    const updatedSubtask = await tasksAPI.updateSubtask(taskId, subtaskId, payload);
    const normalizedSubtask = normalizeSubtask(updatedSubtask);

    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? {
            ...p,
            tasks: (p.tasks || []).map((t) =>
              t.id === taskId
                ? {
                  ...t,
                  subtasks: (t.subtasks || []).map((st) =>
                    st.id === subtaskId ? normalizedSubtask : st
                  ),
                }
                : t
            ),
          }
          : p
      )
    );
  }, [normalizeSubtask]);

  // Delete subtask
  const deleteSubtask = useCallback(async (projectId, taskId, subtaskId) => {
    await tasksAPI.removeSubtask(taskId, subtaskId);
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? {
            ...p,
            tasks: (p.tasks || []).map((t) =>
              t.id === taskId
                ? { ...t, subtasks: (t.subtasks || []).filter((st) => st.id !== subtaskId) }
                : t
            ),
          }
          : p
      )
    );
  }, []);

  // Get all tasks across all projects (for report generation)
  const getAllTasks = useCallback(() => {
    const allTasks = [];
    projects.forEach((project) => {
      (project.tasks || []).forEach((task) => {
        allTasks.push({
          ...task,
          projectId: project.id,
          projectName: project.name,
          progress: calculateTaskProgress(task),
        });
      });
    });
    return allTasks;
  }, [projects]);

  // Get tasks that are in progress or recently updated (for weekly reports)
  const getActiveTasksForReport = useCallback(() => {
    const activeTasks = [];
    projects.forEach((project) => {
      (project.tasks || []).forEach((task) => {
        // Include tasks that are in progress, on hold, or recently completed
        if (task.status !== TASK_STATUS.NOT_STARTED) {
          activeTasks.push({
            taskId: task.id,
            taskName: task.name,
            projectId: project.id,
            projectName: project.name,
            status: task.status,
            progress: calculateTaskProgress(task),
            assignee: task.assignee,
            assigneeId: task.assigneeId,
          });
        }
      });
    });
    return activeTasks;
  }, [projects]);

  // Get project resources
  const getProjectResources = useCallback((projectId) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.resources || [];
  }, [projects]);

  // Add resource to project
  const addResource = useCallback(async (projectId, resourceData) => {
    const payload = {
      projectId,
      type: resourceData.type,
      name: resourceData.name,
      description: resourceData.description,
      details: resourceData.details,
    };
    const createdResource = await resourcesAPI.create(payload);
    const normalizedResource = normalizeResource(createdResource);

    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? { ...p, resources: [...(p.resources || []), normalizedResource] }
          : p
      )
    );
    return normalizedResource;
  }, [normalizeResource]);

  // Update resource
  const updateResource = useCallback(async (projectId, resourceId, updates) => {
    const updatedResource = await resourcesAPI.update(resourceId, updates);
    const normalizedResource = normalizeResource(updatedResource);

    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? {
            ...p,
            resources: (p.resources || []).map((r) =>
              r.id === resourceId ? normalizedResource : r
            ),
          }
          : p
      )
    );
  }, [normalizeResource]);

  // Delete resource
  const deleteResource = useCallback(async (projectId, resourceId) => {
    await resourcesAPI.remove(resourceId);
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? { ...p, resources: (p.resources || []).filter((r) => r.id !== resourceId) }
          : p
      )
    );
  }, []);

  const value = useMemo(
    () => ({
      projects,
      isProjectsLoading,
      projectsError,
      refreshProjects,
      fetchProjectDetails,
      getAllProjects,
      getProjectById,
      getProjectTasks,
      addProject,
      updateProject,
      deleteProject,
      addTask,
      updateTask,
      deleteTask,
      addSubtask,
      updateSubtask,
      deleteSubtask,
      getAllTasks,
      getActiveTasksForReport,
      getProjectResources,
      addResource,
      updateResource,
      deleteResource,
      mockUsers,
      mockTeams,
      TASK_STATUS,
      PROJECT_STATUS,
      RESOURCE_TYPES,
    }),
    [
      projects,
      isProjectsLoading,
      projectsError,
      refreshProjects,
      fetchProjectDetails,
      getAllProjects,
      getProjectById,
      getProjectTasks,
      addProject,
      updateProject,
      deleteProject,
      addTask,
      updateTask,
      deleteTask,
      addSubtask,
      updateSubtask,
      deleteSubtask,
      getAllTasks,
      getActiveTasksForReport,
      getProjectResources,
      addResource,
      updateResource,
      deleteResource,
    ]
  );

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export default ProjectContext;
